import { Component } from '@theme/component';

/**
 * Click-to-play video facade.
 *
 * Nothing but a poster image is in the DOM until someone presses play, so the section never
 * pulls in a third-party player on page load.
 *
 * @extends {Component<{ trigger: HTMLButtonElement }>}
 */
class OudhVideo extends Component {
  play() {
    if (this.hasAttribute('data-playing')) return;

    const url = this.dataset.url ?? '';
    let media;

    if (/\.(?:mp4|webm|mov)(?:\?|$)/i.test(url)) {
      // A file on the shop's own storage: play it directly, no third-party player involved.
      const video = document.createElement('video');
      video.src = url;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      media = video;
    } else {
      const embed = this.#buildEmbedUrl(url);
      if (!embed) return;

      const iframe = document.createElement('iframe');
      iframe.src = embed;
      iframe.title = 'Video';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      media = iframe;
    }

    this.querySelector('.oudh-video__poster')?.remove();
    this.prepend(media);
    this.setAttribute('data-playing', '');
  }

  /**
   * Converts a watch/share URL into its autoplaying embed form.
   *
   * @param {string} url
   * @returns {string | null}
   */
  #buildEmbedUrl(url) {
    if (!url) return null;

    try {
      const parsed = new URL(url);

      if (parsed.hostname.includes('youtu')) {
        // Both youtu.be/<id> and youtube.com/watch?v=<id> reach the same embed path.
        // youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/shorts/<id>, youtube.com/embed/<id>
        const path = parsed.pathname.split('/').filter(Boolean);
        const id =
          parsed.hostname === 'youtu.be'
            ? path[0]
            : (parsed.searchParams.get('v') ?? (['shorts', 'embed', 'live'].includes(path[0]) ? path[1] : null));
        return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
      }

      if (parsed.hostname.includes('vimeo')) {
        const id = parsed.pathname.split('/').filter(Boolean).pop();
        return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
      }
    } catch {
      return null;
    }

    return null;
  }
}

if (!customElements.get('oudh-video')) {
  customElements.define('oudh-video', OudhVideo);
}
